<template>
  <div class="bg-white container mx-auto px-4 sm:px-6 lg:px-8 mb-10">

    <portal to="mobile-header" v-slot="{didToggle, toggle}">
      <div class="flex-grow min-w-0 flex flex-col align-center items-center justify-center h-12">
        <div class="align-center text-lg font-bold px-1 font-display inline-flex sm:hidden text-center truncate">
            All Students
        </div>
      </div>
    </portal>


    <header class="hidden sm:block py-10">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
            All Students
          </h2>
          <div class="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap">
            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6" v-if="students">
              <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <use href="/images/regular.svg#user-graduate"></use>
              </svg>
              {{this.studentList.count}} students
            </div>
            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
              <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <use href="/images/regular.svg#sort-size-down"></use>
              </svg>
              Sorted by&nbsp;
              <PanelDropdown
                :inline="true"
                :items="sortItems"
              >
                <template slot="panel-dropdown__button">
                  {{sortDisplay}}
                </template>
              </PanelDropdown>
            </div>
          </div>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <span class="ml-3 shadow-sm rounded-md">
            <RouterLink :to="{name: 'studentDataloads'}" tag="button" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-cardstock-green-600 hover:bg-cardstock-green-500 focus:outline-none focus:ring-cardstock-green focus:border-cardstock-green-700 active:bg-cardstock-green-700 transition duration-150 ease-in-out cursor-pointer">
              Load Student Data
            </RouterLink>
          </span>
        </div>
      </div>
    </header>
    <div class="p-4 flex flex-col items-center justify-center my-24" v-if="noStudents">
      <div class="text-6xl mb-4">👧🏽👦🏼👧🏻👦🏿</div>
      <div class="font-bold text-lg text-center">You haven't added any kiddos yet.</div>
      <div class="mt-1 text-sm text-center">
        <router-link class="underline text-blue-600" :to="{name: 'studentsNew'}">Head to the student-loader</router-link> to get started!</div>
    </div>
    <div v-else>
      <StudentList
        :students="students"
        paginate
        :canPagePrevious="canPagePrevious"
        :pageNext="pageNext"
        :pagePrevious="pagePrevious"
        :canPageNext="canPageNext"
        :count="studentList.count"
        :page="page"
        :pageSize="studentList.pageSize"
      />
    </div>
  </div>
</template>

<script>
import _map             from 'lodash-es/map'
import _get             from 'lodash-es/get'
import _mapKeys         from 'lodash-es/mapKeys'
import _toArray         from 'lodash-es/toArray'
import _find            from 'lodash-es/find'
import _min             from 'lodash-es/min'
import _max             from 'lodash-es/max'

import Container        from '@/components/Container.vue'
import PageHeader       from '@/components/PageHeader.vue'
import Panel            from '@/components/Panel.vue'
import PanelDropdown    from '@/components/PanelDropdown.vue'
import StudentList      from '@/components/StudentList.vue'
import { preload }      from '@/preloader'
import StudentListQuery from '@/queries/StudentList.gql'

const SORT_NARRATIVES = [
  { narrative: 'alphabetical', sortBy: 'ALPHA', sortDirection: 'ASC' },
  { narrative: 'reverse alphabetical', sortBy: 'ALPHA', sortDirection: 'DESC' },
  { narrative: 'most contacts', sortBy: 'CONTACTS', sortDirection: 'DESC' },
  { narrative: 'fewest contacts', sortBy: 'CONTACTS', sortDirection: 'ASC' },
  { narrative: 'most recent', sortBy: 'RECENT', sortDirection: 'DESC' },
  { narrative: 'least recent', sortBy: 'RECENT', sortDirection: 'ASC' },
]

export default {
  components: {
    Container,
    PageHeader,
    Panel,
    PanelDropdown,
    StudentList
  },

  props: ['teamId', 'currentUser'],

  beforeRouteEnter: (to, from, next) => {
    var pageNumber

    if (to.query.page) {
      pageNumber = parseInt(to.query.page)
    }
    preload({
      callback: next,
      query: StudentListQuery,
      queryName: "studentList",
      variables: {
        page: pageNumber || 1,
        sortBy: to.query.sortBy || 'ALPHA',
        sortDirection: to.query.sortDirection || 'ASC',
        teamId: to.params.teamId
      }
    })
  },

  beforeRouteUpdate: (to, from, next) => {
    var pageNumber

    if (to.query.page) {
      pageNumber = parseInt(to.query.page)
    }
    preload({
      callback: next,
      query: StudentListQuery,
      queryName: "studentList",
      mountedVm: this,
      variables: {
        page: pageNumber,
        sortBy: to.query.sortBy,
        sortDirection: to.query.sortDirection,
        teamId: to.params.teamId
      }
    })
  },

  computed: {
    actionsDropdownItems() {
      return [
        { text: 'Load new students', to: { name: 'studentsNew' } }
      ]
    },

    filter() {
      return this.$route.query.filter || 'currentStudents'
    },

    filterDisplay() {
      if (this.$route.query.teacherPhoneNumberId === undefined) {
        return 'current students'
      } else {
        return _get(this, 'teacherPhoneNumber.displayName')
      }
    },

    filterItems() {

      var items = [
        {text: 'everyone', to: {name: 'students'}},
        {text: 'archived students', to: {name: 'students'}},
        {text: 'students in the trash', to: {name: 'students'}}
      ]

      return items
    },

    noStudents() {
      return _get(this, 'students', []).length == 0
    },

    page() {
      var pageNumber

      if (this.$route.query.page) {
        pageNumber = parseInt(this.$route.query.page)
      }
      return pageNumber || 1
    },

    pageNext() {
      let count = _get(this, 'studentList.count', 0)
      let pageSize = _get(this, 'studentList.pageSize', 1)
      let maxPage = Math.ceil(count / pageSize)

      return {
        name: 'studentsAll',
        query: {
          page: _min([this.page + 1, maxPage]),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        }
      }
    },

    pagePrevious() {
      return {
        name: 'studentsAll',
        query: {
          page: _max([this.page - 1, 1]),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        }
      }
    },

    canPageNext() {
      let count = _get(this, 'studentList.count', 0)
      let pageSize = _get(this, 'studentList.pageSize', 1)
      let remaining = count - this.page * pageSize

      return remaining > 0
    },

    canPagePrevious() {
      return (this.page > 1)
    },

    sortBy() {
      return this.$route.query.sortBy || 'ALPHA'
    },

    sortDirection() {
      return this.$route.query.sortDirection || 'ASC'
    },

    sortDisplay() {
      let foundNarrative = _find(SORT_NARRATIVES, (sortNarrative) => {
        let sortByMatch = sortNarrative.sortBy == this.sortBy
        let sortDirectionMatch = sortNarrative.sortDirection == this.sortDirection

        return sortByMatch && sortDirectionMatch
      })
      return _get(foundNarrative, 'narrative', 'alpha')
    },

    sortItems() {
      return _map(SORT_NARRATIVES, (sortNarrative) => {
        return {
          text: _get(sortNarrative, 'narrative'),
          to: {
            name: 'studentsAll',
            query: {
              sortBy: _get(sortNarrative, 'sortBy'),
              sortDirection: _get(sortNarrative, 'sortDirection')
            }
          }
        }
      })
    },

    students() {
      return _get(this.studentList, 'list', [])
    }

  },

  apollo: {
    studentList: {
      query: StudentListQuery,
      variables() {
        return {
          page: this.page,
          sortBy: this.sortBy,
          sortDirection: this.sortDirection,
          teamId: this.teamId
        }
      },
      fetchPolicy: 'cache-first'
    }
  }
}
</script>
